import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  UPLOAD_COLUMNS,
  deleteUploadToolRecordContract,
} from "./upload-layouts.contract";

/**
 * Validation rules for the Results → Upload tool: org-wide checks a pricing
 * user writes against the AS400 Header Cross upload CSV before downloading it.
 *
 * A rule targets one upload column by its spreadsheet letter and applies one
 * check to the value that column will carry in the CSV — after the mapping and
 * the calculated columns, so `AS` (new cost discount) can be checked too. The
 * rules are evaluated in the browser; the server only stores them.
 */

/** Rules can check any upload column. */
export const UPLOAD_RULE_COLUMNS = UPLOAD_COLUMNS;

export const UploadRuleColumnSchema = z.enum(UPLOAD_RULE_COLUMNS);

export const UploadRuleCheckSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("required") }),
  z.object({ type: z.literal("equals"), value: z.string().max(255) }),
  z.object({
    type: z.literal("oneOf"),
    values: z.array(z.string().max(255)).min(1).max(200),
  }),
  z
    .object({
      type: z.literal("numberBetween"),
      min: z.number().finite().nullable(),
      max: z.number().finite().nullable(),
    })
    .refine((check) => check.min !== null || check.max !== null, {
      message: "Set a minimum, a maximum, or both",
    })
    .refine(
      (check) =>
        check.min === null || check.max === null || check.min <= check.max,
      { message: "The minimum must not exceed the maximum" },
    ),
  z.object({
    type: z.literal("maxLength"),
    max: z.number().int().min(0).max(1000),
  }),
]);

/** Block stops the download; Warn lets it through after a confirmation. */
export const UploadRuleSeveritySchema = z.enum(["block", "warn"]);

export const UploadRuleSchema = z.object({
  id: z.string().uuid(),
  column: UploadRuleColumnSchema,
  check: UploadRuleCheckSchema,
  severity: UploadRuleSeveritySchema,
  enabled: z.boolean(),
  /** The supplier layout this rule applies to; null = every layout. */
  layoutId: z.string().uuid().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const CreateUploadRuleInputSchema = z.object({
  column: UploadRuleColumnSchema,
  check: UploadRuleCheckSchema,
  severity: UploadRuleSeveritySchema,
  enabled: z.boolean().default(true),
  layoutId: z.string().uuid().nullable().default(null),
});

export const UpdateUploadRuleInputSchema = z.object({
  id: z.string().uuid(),
  column: UploadRuleColumnSchema.optional(),
  check: UploadRuleCheckSchema.optional(),
  severity: UploadRuleSeveritySchema.optional(),
  enabled: z.boolean().optional(),
  layoutId: z.string().uuid().nullable().optional(),
});

export const uploadRulesContracts = {
  list: oc
    .input(z.object({}))
    .output(
      z.object({
        rules: z.array(UploadRuleSchema),
        /**
         * Stored rules this contract can no longer read (written by another
         * version). Reported rather than dropped, so they can be deleted and
         * so a download is never checked against half the rules.
         */
        unreadableRuleIds: z.array(z.string().uuid()),
      }),
    )
    .route({
      method: "GET",
      summary: "List the org's Results → Upload validation rules",
      description:
        "Returns the organization's checks applied to ERP price upload CSV columns before download (required, equals, one-of, range, max length), plus ids of unreadable stored rules.",
    }),
  create: oc
    .input(CreateUploadRuleInputSchema)
    .output(z.object({ rule: UploadRuleSchema }))
    .route({
      method: "POST",
      summary: "Create an upload validation rule",
      description:
        "Adds a check on one price upload column, with block or warn severity, optionally scoped to one supplier layout. Rules run in the browser before download.",
    }),
  update: oc
    .input(UpdateUploadRuleInputSchema)
    .output(z.object({ rule: UploadRuleSchema }))
    .route({
      method: "PATCH",
      summary: "Update an upload validation rule",
      description:
        "Changes an upload validation rule's column, check, severity, enabled flag, or supplier layout scope.",
    }),
  delete: deleteUploadToolRecordContract(
    "Delete an upload validation rule",
    "Permanently removes one price upload validation rule from the organization.",
  ),
} as const;

export type UploadRuleColumn = z.infer<typeof UploadRuleColumnSchema>;
export type UploadRuleCheck = z.infer<typeof UploadRuleCheckSchema>;
export type UploadRuleSeverity = z.infer<typeof UploadRuleSeveritySchema>;
export type UploadRule = z.infer<typeof UploadRuleSchema>;
export type CreateUploadRuleInput = z.infer<typeof CreateUploadRuleInputSchema>;
export type UpdateUploadRuleInput = z.infer<typeof UpdateUploadRuleInputSchema>;
